import {i18n} from "./i18n";
import UrlHelpers from "./url";
import {FieldHandler} from "./fieldHandler";
import {create_orga_request_dto} from "./organizationList";

export class App {
	private _dapi: Dapi = (<any>window).dapi;
	private _i18n: i18n = new i18n();
	private _animationActive = false;

	constructor() {
		this._setModalActions();
		this._tenantSelector_changed();
		const btnSubscribe = document.getElementById('btnAppSubscribe');
		if(btnSubscribe) {
			(<HTMLButtonElement>btnSubscribe).addEventListener('click', this._openWizard)
		}
		this.setDapiFullscreenMode();
	}

	private _openWizard = (e:any):void => {
		let wizardUrl = UrlHelpers.getLinkRelHref('wizard');
		if(wizardUrl){
			this._dapi.activateSingleResourceMode();
			this._dapi.navigate(wizardUrl);
		}else {
			console.warn('Link relation "wizard" not found or empty!');
		}
	};

	private setDapiFullscreenMode = (): void => {
		if (!this._dapi) {
			console.error("dapi not found");
			return;
		}
		this._dapi.setResourceVisibilityChangedCallback(isResourceVisible => {
			if (isResourceVisible) {
				this._dapi.deactivateSingleResourceMode();
			}
		})
	};

	private _setModalActions = ():void => {
		let tenantSelector = <HTMLSelectElement>document.getElementById('modalSubscribeTenantSelector');
		if(tenantSelector){
			tenantSelector.addEventListener('change', this._tenantSelector_changed);
		}
		let btnSubscribe = <HTMLButtonElement>document.getElementById('modalSubscribeBtnSubscribe');
		if(btnSubscribe){
			btnSubscribe.addEventListener('click', this._btnModalSubscribe_click);
		}
		let btnCancel = <HTMLButtonElement>document.getElementById('modalSubscribeBtnCancel');
		if (btnCancel){
			btnCancel.addEventListener('click', () => {
				this._closeSubscribeAnimation();
				this._closeSubscribeAnimationControls();
				this._setSubscribeButtonDisabledState(false);
			});
		}
		let chkProviderAllowed = <HTMLInputElement>document.getElementById('modalSubscribeProviderAllowed');
		if(chkProviderAllowed){
			chkProviderAllowed.addEventListener('click', this._chkProviderAllowed_click);
		}
	};

	private _chkProviderAllowed_click = (e:any):void => {
		let element = e.currentTarget;
		this._setSubscribeButtonDisabledState(!element.checked);
	};

	private _tenantSelector_changed = (): void => {
		let selectedTenant = FieldHandler.getFieldValue('modalSubscribeTenantSelector');
		let createTenantDiv = <HTMLDivElement>document.getElementById('modalSubscribeCreateTenant');
		let createOrganizationDiv = <HTMLDivElement>document.getElementById('modalSubscribeCreateOrganization');
		if (createTenantDiv && createOrganizationDiv) {
			switch (selectedTenant) {
				case "-1":
					createTenantDiv.classList.remove('collapse');
					createOrganizationDiv.classList.add('collapse');
					break;
				case "-2":
					createTenantDiv.classList.remove('collapse');
					createOrganizationDiv.classList.remove('collapse');
					break;
				default:
					createTenantDiv.classList.add('collapse');
					createOrganizationDiv.classList.add('collapse');
			}
		}
	};

	private _btnModalSubscribe_click = (): void => {
		let chkProviderAllowed = <HTMLInputElement>document.getElementById('modalSubscribeProviderAllowed');
		if(chkProviderAllowed.checked) {
			let selectedTenant = FieldHandler.getFieldValue('modalSubscribeTenantSelector');
			let tenantSelector = <HTMLSelectElement>document.getElementById('modalSubscribeTenantSelector');
			let selectedOption = tenantSelector.selectedOptions[0];
			FieldHandler.clearFieldError('tenantDomain');
			FieldHandler.clearFieldError('orgaName');
			switch (selectedTenant) {
				case "-1":
					let orgaId = selectedOption.dataset.id;
					let orgaUrl = selectedOption.dataset.url;
					if (this._inputIsEmpty('tenantDomainValue')) {
						FieldHandler.setFieldError('tenantDomain',this._i18n.getTranslation('tenant_subdomain_error_field_empty'));
						return;
					}
					this._modalCreateTenantAndSubscribe(orgaUrl, orgaId);
					break;
				case "-2":
					let orgaListUrl = selectedOption.dataset.url;
					if (this._inputIsEmpty('tenantDomainValue')) {
						FieldHandler.setFieldError('tenantDomain',this._i18n.getTranslation('tenant_subdomain_error_field_empty'));
						return;
					}
					if (this._inputIsEmpty('orgaName')) {
						FieldHandler.setFieldError('orgaName',this._i18n.getTranslation('organization_edit_requiredfield_name_empty'));
						return
					}
					this._modalCreateOrganizationAndTenantAndSubscribe(orgaListUrl);
					break;
				default:
					this._modalSubscribeApp(selectedTenant);
			}
			this._setSubscribeButtonDisabledState(true);
			this._subscribeStartAnimation(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_begin'));
		} else {
			console.log('checkbox provider allowed not checked!');
		}
	};

	private _modalSubscribeApp = (selectedTenant:string): void => {
		this._subscribeStartAnimation(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_subscribing_app'));
		let subscriptionUrl = UrlHelpers.getLinkRelHref("subscription");
		let appName = UrlHelpers.getMetaData("appName");
		let xhr = new XMLHttpRequest();
		xhr.open('PUT', subscriptionUrl);
		xhr.setRequestHeader('accept', 'application/json');
		xhr.onload = () => {
			if(xhr.status === 200) {
				const data = JSON.parse(xhr.responseText);
				this._subscribeSuccess(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_success'), `https://${data.fullQualifiedDomain}`);
				return;
			}
			console.error(`Got error from server: ${xhr.responseText}`);
			//Misc errors
			let errorMessage = this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_error_miscellaneous');
			//Bad Request
			if (xhr.status == 400) {
				errorMessage = this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_error_badrequest');
			}
			//Unauthorized
			if (xhr.status == 401 || xhr.status == 403) {
				errorMessage = this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_error_unauthorized');
			}
			this._subscribeError(errorMessage);
		};
		let data:subscriptionRequestDto = {
			action: 'subscribe',
			tenantId: selectedTenant,
			appName:appName
		};
		xhr.send(JSON.stringify(data));
	};

	private _modalCreateTenantAndSubscribe = (url: string, orgaId: string):void => {
		this._subscribeStartAnimation(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_new_tenant'));
		let btnSubscribe = <HTMLButtonElement>document.getElementById('modalSubscribeBtnSubscribe');
		let domain = FieldHandler.getFieldValue('tenantDomainValue');
		let checkUrl = UrlHelpers.getLinkRelHref('domainCheck');
		let req = new Request(checkUrl, {
			method:'post',
			headers: {
				'accept':'application/json'
			},
			body: JSON.stringify({
				domain: domain,
				tenantId: ''
			})
		});
		fetch(req,{credentials: "same-origin"})
			.then((response) => {
				if(response.status == 200) {
					let result = response.json();
					result.then(value => {
						console.log(value);
						if (value.exists) {
							let message:string;
							if(value.message === "Domain already exists.") {
								message = this._i18n.getTranslation('tenant_subdomain_error_domain_already_exist');
							} else if(value.message === "Domain is blacklisted.") {
								message = this._i18n.getTranslation('tenant_subdomain_error_domain_is_blacklisted');
							} else if(value.message === "Domain contains invalid characters.") {
								message = this._i18n.getTranslation('tenant_subdomain_error_invalidchar');
							} else {
								message = this._i18n.getTranslation('tenant_subdomain_error_domain_already_exist');
							}
							console.error(value.message);
							this._subscribeError(message);
							FieldHandler.setFieldError('tenantDomain', message);
							return
						} else {
							let xhr = new XMLHttpRequest();
							xhr.open('POST', url);
							xhr.setRequestHeader('accept', 'application/json');
							xhr.onload = () => {
								if (xhr.status === 201) {
									let targetUri = xhr.getResponseHeader('location');
									let tenantId = xhr.responseText.replace(/"/g, '');
									console.log('create new tenant success');
									this._modalUpdateCreatedTenant(targetUri, tenantId, orgaId);
								} else {
									console.error('create new tenant failed: ' + xhr.responseText);
									btnSubscribe.removeAttribute('disabled');
									this._subscribeError(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_error_miscellaneous'));
								}
							};
							xhr.send();
						}
					});
				} else {
					FieldHandler.setFieldError('tenantDomain', this._i18n.getTranslation('tenant_subdomain_error_unexpected_error'));
					console.error(response.statusText);
					btnSubscribe.removeAttribute('disabled');
                    this._subscribeError(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_error_miscellaneous'));
				}
			}).catch(error => {
			FieldHandler.setFieldError('tenantDomain', this._i18n.getTranslation('tenant_subdomain_error_unexpected_error'));
			console.error(error);
			btnSubscribe.removeAttribute('disabled');
            this._subscribeError(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_error_miscellaneous'));
		});
	};

	private _modalUpdateCreatedTenant = (url: string, tenantId: string, orgaId: string) => {
		const data = {
			tenant: {
				id:tenantId,
				domainName:FieldHandler.getFieldValue('tenantDomainValue'),
				organizationId:orgaId
			}
		};
		let xhr = new XMLHttpRequest();
		xhr.open('PUT', url);
		xhr.setRequestHeader('accept', 'application/json');
		xhr.onload = () => {
			if (xhr.status === 200) {
				console.log('update tenant success');
				this._modalSubscribeApp(tenantId);
			} else {
				console.error('update created tenant failed: '+xhr.responseText);
				let btnSubscribe = <HTMLButtonElement>document.getElementById('modalSubscribeBtnSubscribe');
				if(btnSubscribe){
					btnSubscribe.removeAttribute('disabled');
				}
                this._subscribeError(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_error_miscellaneous'));
			}
		};
		xhr.send(JSON.stringify(data));
	};

	private _modalCreateOrganizationAndTenantAndSubscribe = (url: string):void => {
		this._subscribeStartAnimation(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_new_organization'));
		let orgaName = FieldHandler.getFieldValue('orgaName');
		let dto: create_orga_request_dto = {
			name: orgaName
		};
		let xhr = new XMLHttpRequest();
		xhr.open('POST', url);
		xhr.setRequestHeader('accept', 'application/json');
		xhr.onload = ()=> {
			if (xhr.status === 201) {
				let targetUri = xhr.getResponseHeader('location');
				let orgaId = xhr.responseText.replace(/"/g, '');
				console.log('create new organization success.');
				this._modalCreateTenantAndSubscribe(targetUri, orgaId);
			} else {
				console.error('create new organisation failed: '+xhr.responseText);
				let btnSubscribe = <HTMLButtonElement>document.getElementById('modalSubscribeBtnSubscribe');
				if(btnSubscribe){
					btnSubscribe.removeAttribute('disabled');
				}
                this._subscribeError(this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_error_miscellaneous'));
			}
		};
		xhr.send(JSON.stringify(dto));
	};

	private _inputIsEmpty = (inputId:string): boolean => {
		return FieldHandler.getFieldValue(inputId).isNullOrEmpty();
	};

    private _subscribeStartAnimation = (message:string) => {
    	if (!this._animationActive) {
    		this._closeSubscribeAnimationControls();
			this._startAnimation();
			this._openSubscribeAnimation();
			this._animationActive = true;
		}
        this._setSubscribeAnimationMessage(message);
    };

	private _subscribeSuccess = (message: string, targetUrl: string) => {
		const btn = <HTMLAnchorElement>document.createElement('a');
		btn.href = targetUrl;
		btn.target = '_blank';
		btn.innerHTML = this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_btn_goto_cloud');

		const secondsToWaitForIDP = 15;
		setTimeout(() => {
			this._setSubscribeAnimationMessageAndButton(message, btn).then(() => {
				this._animationSuccess();
				this._openSubscribeAnimationControls();
			});
		}, secondsToWaitForIDP * 1000);
	};

	private _subscribeError = (message: string) => {
		const btn = <HTMLButtonElement>document.createElement('button');
		btn.innerHTML = this._i18n.getTranslation('tenantSubscriptionsItem_subscription_animation_message_subscription_btn_back');
		btn.addEventListener('click', () => {
			this._closeSubscribeAnimationControls();
			this._closeSubscribeAnimation();
			this._setSubscribeButtonDisabledState(false);
		});

		this._setSubscribeAnimationMessageAndButton(message, btn).then(() => {
			this._animationError();
			this._openSubscribeAnimationControls();
		});
	};

	private _setSubscribeAnimationMessageAndButton = (message: string, newElement: HTMLButtonElement | HTMLAnchorElement): Promise<any> => {
		return new Promise((resolve) => {
			this._setSubscribeAnimationMessage(message);
			this._replaceSubscribeAnimationButton(newElement);
			resolve();
		});
	};

	private _setSubscribeAnimationMessage = (message: string) => {
		const h = <HTMLHeadingElement>document.getElementById('modalSubscribeAnimation-message');
		h.innerHTML = message;
	};

	private _replaceSubscribeAnimationButton = (newElement: HTMLButtonElement | HTMLAnchorElement) => {
		const button = <HTMLButtonElement | HTMLAnchorElement>document.getElementById('modalSubscribeAnimation-button');
		newElement.id = button.id;
		newElement.type = button.type;
		Array.apply(null, button.classList).forEach((c) => {
			newElement.classList.add(c);
		});
		button.replaceWith(newElement);
	};

	private _setAnimationImageSource = (src: string, spin: boolean) => {
		const image = <HTMLImageElement>document.getElementById('modalSubscribeAnimation-icon');
		image.src = src;
		spin ? image.classList.add('dv-spin') : image.classList.remove('dv-spin');
	};

	private _startAnimation = () => {
		const src = 'https://s3.eu-central-1.amazonaws.com/mediamanager.cloudcenter.service.d-velop.cloud/2c5098e9-52ee-44c2-ba65-19f19d9fe535';
		this._setAnimationImageSource(src, true);
		this._animationActive = true;
	};

	private _animationSuccess = () => {
		const src = 'https://s3.eu-central-1.amazonaws.com/mediamanager.cloudcenter.service.d-velop.cloud/9e332407-d3cf-442f-a01a-9037976519ab';
		this._setAnimationImageSource(src, false);
		this._animationActive = false;
	};

	private _animationError = () => {
		const src = 'https://s3.eu-central-1.amazonaws.com/mediamanager.cloudcenter.service.d-velop.cloud/bd28bb14-20e4-47ae-8a7d-688b56ed5eda';
		this._setAnimationImageSource(src, false);
		this._setSubscribeButtonDisabledState(false);
		this._animationActive = false;
	};

	private _openSubscribeAnimationControls = () => {
		console.log('Open animation controls');
		const controls = <HTMLDivElement>document.getElementById('modalSubscribeAnimation-controls');
		controls.classList.add('in');
	};

	private _closeSubscribeAnimationControls = () => {
		const controls = <HTMLDivElement>document.getElementById('modalSubscribeAnimation-controls');
		controls.classList.remove('in');
	};

	private _openSubscribeAnimation = () => {
		(<HTMLDivElement>document.getElementById('modalSubscribeAnimation-container')).classList.add('in');
		(<HTMLDivElement>document.getElementById('modalSubscribeProviderInfo-container')).classList.remove('in');
	};

	private _closeSubscribeAnimation = () => {
		(<HTMLDivElement>document.getElementById('modalSubscribeAnimation-container')).classList.remove('in');
		(<HTMLDivElement>document.getElementById('modalSubscribeProviderInfo-container')).classList.add('in');
	};

	private _setSubscribeButtonDisabledState = (disable: boolean) => {
		let btnSubscribe = <HTMLButtonElement>document.getElementById('modalSubscribeBtnSubscribe');
		if (btnSubscribe) {
			btnSubscribe.disabled = disable;
		}
	};
}


export interface subscriptionRequestDto {
	action:string
	tenantId:string
	appName:string
}

export interface create_tenant_request_dto{
	tenant: create_tenant_details
}

export interface create_tenant_details {
	id: string;
	domainName: string;
	organizationId: string;
}
